/**
 * AI automation: Connect's own side of every client's AI assistant.
 *
 * - **The gateway** — whether IngeniousPro's AI gateway answers Connect's key.
 *   The key itself is an environment secret and is never shown.
 * - **Platform rules and limits** — the outer boundary. Every client's rules
 *   and limits sit inside these; the model reads these last, as outranking
 *   everything a client wrote. The kill switch stops every assistant at once.
 * - **The rate card** — what a client pays per million tokens, by model. The
 *   gateway meters; Connect prices and charges the client's wallet.
 * - **Clients** — who has AI, their balances and use.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, CircleX, IndianRupee, Plus, RefreshCw, Server, ShieldCheck, Trash2, Users } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Combobox,
  Input,
  PageHeader,
  QueryPending,
  ReadOnly,
  ScrollArea,
  Select,
  Sections,
  Switch,
  Textarea,
  api,
  formatMoney,
  toast,
  useCan,
} from '@connect/web-shared';

import { aiKeys } from './ai-card';

interface Platform {
  enabled: boolean;
  rules: string[];
  model: string | null;
  temperature: number;
  max_output_tokens: number;
  context_tokens: number;
  history_messages: number;
  max_steps_per_turn: number;
  max_tool_calls_per_turn: number;
  max_turns_per_hour: number;
  debounce_seconds: number;
  default_reactivate_minutes: number;
  confirmation_minutes: number;
  tool_result_chars: number;
  minimum_balance: string;
  memory_max_keys: number;
  max_failures: number;
  fallback_message: string;
  /** Staff's own figures per conversation channel; a channel left out keeps what it declares. */
  channels: Record<string, ChannelLimits>;
  /** How much a thinking model thinks, on a channel that declares none; null: the model's default. */
  reasoning: Reasoning | null;
  /** Staff's own thinking for Connect's AI tasks; a task left out thinks as it declares. */
  task_reasoning: Partial<Record<string, Reasoning>>;
}

/** The AI gateway's `reasoning` (`PlatformPolicy.reasoning` on the API). */
type Reasoning = 'off' | 'low' | 'medium' | 'high';

const THINKING: { value: Reasoning; label: string }[] = [
  { value: 'off', label: 'Off (as little as the model allows)' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

function thinkingLabel(value: Reasoning | null): string {
  return THINKING.find((t) => t.value === value)?.label.toLowerCase() ?? "the model's default";
}

interface ChannelLimits {
  answer_tokens: number | null;
  history_messages: number | null;
  reasoning: Reasoning | null;
}

const NO_LIMITS: ChannelLimits = { answer_tokens: null, history_messages: null, reasoning: null };

/** A conversation channel, with what it declares for itself. */
interface ChannelDefaults {
  id: string;
  label: string;
  answer_tokens: number;
  history_messages: number;
  reasoning: Reasoning | null;
}

/** One of Connect's own AI tasks, with the thinking it declares. */
interface AiTaskDefaults {
  id: string;
  label: string;
  reasoning: Reasoning | null;
}

interface Pricing {
  currency: 'INR';
  input_per_million: string;
  output_per_million: string;
  models: { match: string; input_per_million: string; output_per_million: string }[];
}

interface Overview {
  platform: Platform;
  channels: ChannelDefaults[];
  tasks: AiTaskDefaults[];
  pricing: Pricing;
  gateway: { base_url: string; configured: boolean };
}

interface ClientRow {
  public_id: string;
  name: string;
  enabled: boolean;
  balance: string;
  low_balance_threshold: string | null;
  spent_30_days: string;
  custom_pricing: boolean;
  agents: number;
  active_agents: number;
}

const NUMBERS: { key: keyof Platform; label: string; hint: string; step?: string }[] = [
  { key: 'default_reactivate_minutes', label: 'Default quiet time (min)', hint: 'After a person replies, before the assistant takes a chat back.' },
  { key: 'confirmation_minutes', label: 'Confirmation valid (min)', hint: 'How long a customer has to confirm a change.' },
  { key: 'history_messages', label: 'Messages read per answer', hint: 'Newest first, on a service that sets none below.' },
  { key: 'max_steps_per_turn', label: 'Model calls per answer', hint: 'Each may request system calls.' },
  { key: 'max_tool_calls_per_turn', label: 'System calls per answer', hint: 'Clients may set fewer.' },
  { key: 'max_turns_per_hour', label: 'Answers per chat per hour', hint: 'Above it, a person is asked for.' },
  { key: 'max_failures', label: 'Silent retries', hint: 'Failed answers before apologising and handing over.' },
  { key: 'tool_result_chars', label: 'Answer size read', hint: "Characters of one system's answer." },
  { key: 'memory_max_keys', label: 'Facts per customer', hint: 'Remembered across conversations.' },
  { key: 'max_output_tokens', label: 'Least answer room (tokens)', hint: 'Every answer keeps it when history is fitted in; a service that sets none below gets it.' },
  { key: 'temperature', label: 'Temperature', hint: '0 — steady, 1 — varied.', step: '0.1' },
  { key: 'debounce_seconds', label: 'Wait for a burst (s)', hint: 'Messages sent together are answered once.', step: '0.5' },
];

export function AiAutomationPage() {
  const overview = useQuery({ queryKey: aiKeys.overview, queryFn: () => api.get<Overview>('/messaging/ai') });
  const clients = useQuery({ queryKey: aiKeys.clients, queryFn: () => api.get<ClientRow[]>('/messaging/ai/clients') });
  if (overview.data === undefined) return <QueryPending query={overview} label="Loading AI automation" />;
  return (
    <div className="space-y-4">
      <PageHeader
        title="AI automation"
        description="The boundary every client's AI assistant works inside, what it costs, and who has it."
      />
      <Sections
        label="AI automation"
        items={[
          {
            id: 'gateway',
            label: 'AI gateway',
            hint: 'Where the models are',
            icon: <Server />,
            content: <GatewayCard configured={overview.data.gateway.configured} baseUrl={overview.data.gateway.base_url} />,
          },
          {
            id: 'platform',
            label: 'Platform limits',
            hint: 'The boundary for every client',
            icon: <ShieldCheck />,
            content: <PlatformCard initial={overview.data.platform} channels={overview.data.channels} tasks={overview.data.tasks} />,
          },
          {
            id: 'pricing',
            label: 'Pricing',
            hint: 'What AI costs clients',
            icon: <IndianRupee />,
            content: <PricingCard initial={overview.data.pricing} />,
          },
          {
            id: 'clients',
            label: 'Clients with AI',
            hint: 'Who has it, and their wallets',
            icon: <Users />,
            content: (
              <Card>
                <CardHeader title="Clients with AI" description="Switch AI on for a client, and top up its wallet, on the client's page." />
                <CardBody>
                  {!clients.data || clients.data.length === 0 ? (
                    <p className="text-sm text-muted">No client has AI yet.</p>
                  ) : (
                    // Five columns: on a phone the table scrolls sideways, not the page.
                    <ScrollArea>
                    <table className="w-full min-w-[34rem] text-sm">
                      <thead className="text-left text-2xs uppercase tracking-wide text-muted">
                        <tr>
                          <th className="py-2">Client</th>
                          <th className="py-2">AI</th>
                          <th className="py-2">Assistants</th>
                          <th className="py-2 text-right">Balance</th>
                          <th className="py-2 text-right">Last 30 days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clients.data.map((c) => (
                          <tr key={c.public_id} className="border-t border-line">
                            <td className="py-2">
                              <Link to={`/clients/${c.public_id}?tab=services`} className="text-accent hover:underline">{c.name}</Link>
                            </td>
                            <td className="py-2">
                              <span className="inline-flex gap-1">
                                {c.enabled ? <Badge tone="success">On</Badge> : <Badge>Off</Badge>}
                                {c.custom_pricing ? <Badge tone="info">Own price</Badge> : null}
                              </span>
                            </td>
                            <td className="py-2 text-muted">{c.active_agents} of {c.agents} answering</td>
                            <td className="py-2 text-right font-mono tabular">{formatMoney(c.balance)}</td>
                            <td className="py-2 text-right font-mono tabular">{formatMoney(c.spent_30_days)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </ScrollArea>
                  )}
                </CardBody>
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
}

function GatewayCard({ configured, baseUrl }: { configured: boolean; baseUrl: string }) {
  const status = useQuery({
    queryKey: aiKeys.gateway,
    enabled: configured,
    queryFn: () =>
      api.get<{ configured: boolean; reachable: boolean; detail: string; models?: string[] }>('/messaging/ai/gateway'),
  });
  return (
    <Card>
      <CardHeader
        title="AI gateway"
        description={<span className="font-mono">{baseUrl}</span>}
        actions={configured ? (
          <Button size="sm" variant="ghost" loading={status.isFetching} onClick={() => void status.refetch()}>
            <RefreshCw className="size-3.5" />
            Check
          </Button>
        ) : null}
      />
      <CardBody className="text-sm">
        {!configured ? (
          <Alert tone="danger" title="Not set up">
            AI_GATEWAY_API_KEY is not set on this installation, so no assistant can answer. It is an environment secret —
            see the deployment guide.
          </Alert>
        ) : status.data ? (
          <div className="space-y-1">
            <p className="flex items-center gap-1.5">
              {status.data.reachable ? <CircleCheck className="size-4 text-success" /> : <CircleX className="size-4 text-danger" />}
              {status.data.reachable ? 'Answering Connect’s key' : `Not answering: ${status.data.detail}`}
            </p>
            {status.data.models?.length ? (
              <p className="text-xs text-muted">Models: {status.data.models.join(', ')}</p>
            ) : null}
          </div>
        ) : (
          // The check itself failing is not "Checking…" for ever: it says so,
          // and Check runs it again.
          <QueryPending query={status} loading={<p className="text-muted">Checking…</p>} />
        )}
      </CardBody>
    </Card>
  );
}

/**
 * The context window sizes offered while typing — suggestions, never the only
 * choices: any whole number in range is accepted, so a model or GPU that holds
 * more later needs no change here. 8192 is the default, what the local
 * qwen2.5:14b holds on the gateway's RTX A2000 12 GB; the cloud sizes are the
 * common windows of hosted models. The range is the API's own
 * (`PlatformPolicy.context_tokens`).
 */
export const CONTEXT_SIZES = ['4096', '8192', '16384', '32768', '65536', '128000', '200000', '1000000', '2000000'];
const CONTEXT_MIN = 2048;
const CONTEXT_MAX = 2_000_000;

export function contextProblem(text: string): string | undefined {
  if (!/^\d+$/.test(text.trim())) return 'A whole number of tokens.';
  const tokens = Number(text);
  if (tokens < CONTEXT_MIN) return `At least ${CONTEXT_MIN.toLocaleString('en-IN')}.`;
  if (tokens > CONTEXT_MAX) return `At most ${CONTEXT_MAX.toLocaleString('en-IN')}.`;
  return undefined;
}

function PlatformCard({ initial, channels, tasks }: { initial: Platform; channels: ChannelDefaults[]; tasks: AiTaskDefaults[] }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.ai.edit');
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);
  const set = <K extends keyof Platform>(key: K, value: Platform[K]) => setForm((f) => ({ ...f, [key]: value }));
  // An empty box is the channel's own figure: sent as null, never as 0.
  const own = (id: string): ChannelLimits => ({ ...NO_LIMITS, ...form.channels[id] });
  const setChannel = (id: string, patch: Partial<ChannelLimits>) =>
    setForm((f) => ({ ...f, channels: { ...f.channels, [id]: { ...NO_LIMITS, ...f.channels[id], ...patch } } }));
  const count = (text: string) => (text.trim() === '' ? null : Number(text));
  // Typed as text, so a size half typed ("12…" on the way to 128000) is not
  // refused; it reaches the form only once it is a size the API takes.
  const [contextText, setContextText] = useState(String(initial.context_tokens));
  useEffect(() => setContextText(String(initial.context_tokens)), [initial]);
  const contextError = contextProblem(contextText);
  const save = useMutation({
    mutationFn: () => api.put<Overview>('/messaging/ai/platform', form),
    onSuccess: (data) => {
      queryClient.setQueryData(aiKeys.overview, data);
      toast.success('Platform rules saved');
    },
    onError: (e) => toast.error('Not saved', e instanceof ApiError ? e.message : undefined),
  });
  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title="Platform rules and limits"
          description="Apply to every client's assistant, and outrank anything a client writes."
          actions={
            <Switch label={form.enabled ? 'AI on' : 'AI off everywhere'} checked={form.enabled}
              onChange={(e) => set('enabled', e.target.checked)} />
          }
        />
        <CardBody className="space-y-4">
          {!form.enabled ? (
            <Alert tone="warning">Saved like this, no assistant answers anybody; every conversation is left to people.</Alert>
          ) : null}
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted">Rules every assistant follows</p>
            {form.rules.map((rule, index) => (
              <div key={index} className="flex gap-2">
                <Textarea rows={2} containerClassName="flex-1" value={rule} maxLength={600} aria-label="Rule"
                  onChange={(e) => set('rules', form.rules.map((r, i) => (i === index ? e.target.value : r)))} />
                {canEdit ? (
                  <Button size="sm" variant="ghost" aria-label="Remove" onClick={() => set('rules', form.rules.filter((_, i) => i !== index))}>
                    <Trash2 className="size-3.5" />
                  </Button>
                ) : null}
              </div>
            ))}
            {canEdit ? (
              <Button size="sm" variant="secondary" onClick={() => set('rules', [...form.rules, ''])}>
                <Plus className="size-3.5" />
                Add a rule
              </Button>
            ) : null}
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <Input label="Model (provider:model)" placeholder="The gateway's default"
              value={form.model ?? ''} onChange={(e) => set('model', e.target.value.trim() || null)} />
            <Select label="Thinking" hint="How much a model that thinks before it answers thinks, on a service that sets none below."
              value={form.reasoning ?? ''}
              options={[{ value: '', label: "The model's default" }, ...THINKING]}
              onChange={(e) => set('reasoning', (e.target.value || null) as Reasoning | null)} />
            <Input label="Minimum balance to answer (₹)" inputMode="decimal"
              value={form.minimum_balance} onChange={(e) => set('minimum_balance', e.target.value)} />
            <Combobox
              label="Context window (tokens)"
              hint="What the model is served with; prompts are fitted inside it. 8192 for the local model on the A2000 12 GB — larger for a hosted model or a bigger GPU."
              inputMode="numeric"
              options={CONTEXT_SIZES}
              emptyLabel="Not a suggested size — any size in range can be saved."
              value={contextText}
              error={contextError}
              onChange={(text) => {
                setContextText(text);
                if (!contextProblem(text)) set('context_tokens', Number(text));
              }}
            />
            {NUMBERS.map(({ key, label, hint, step }) => (
              <Input key={key} label={label} hint={hint} type="number" step={step ?? '1'}
                value={String(form[key] ?? '')}
                onChange={(e) => set(key, Number(e.target.value) as never)} />
            ))}
          </div>
          {channels.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted">Per service: how much of a conversation is read, and the room to answer</p>
              <p className="text-xs text-muted">
                Empty keeps the service's own figure, shown in grey. Both sides count, so the 20th message of a chat is 40 messages
                read. The room includes a model's thinking and costs nothing unused; the context window still decides what fits.
                Thinking is billed as output and is most of a reply's wait: low answers short questions as well, and faster.
              </p>
              {channels.map((c) => (
                <div key={c.id} className="grid items-end gap-3 md:grid-cols-3">
                  <Input label={`${c.label}: messages read`} type="number" step="1" placeholder={String(c.history_messages)}
                    value={own(c.id).history_messages == null ? '' : String(own(c.id).history_messages)}
                    onChange={(e) => setChannel(c.id, { history_messages: count(e.target.value) })} />
                  <Input label={`${c.label}: answer room (tokens)`} type="number" step="100" placeholder={String(c.answer_tokens)}
                    value={own(c.id).answer_tokens == null ? '' : String(own(c.id).answer_tokens)}
                    onChange={(e) => setChannel(c.id, { answer_tokens: count(e.target.value) })} />
                  <Select label={`${c.label}: thinking`} value={own(c.id).reasoning ?? ''}
                    options={[{ value: '', label: `Its own (${thinkingLabel(c.reasoning)})` }, ...THINKING]}
                    onChange={(e) => setChannel(c.id, { reasoning: (e.target.value || null) as Reasoning | null })} />
                </div>
              ))}
            </div>
          ) : null}
          {tasks.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted">Connect's own AI tasks: how much the model thinks</p>
              <div className="grid items-end gap-3 md:grid-cols-3">
                {tasks.map((t) => (
                  <Select key={t.id} label={`${t.label}: thinking`} value={form.task_reasoning?.[t.id] ?? ''}
                    options={[{ value: '', label: `Its own (${thinkingLabel(t.reasoning)})` }, ...THINKING]}
                    onChange={(e) => {
                      const { [t.id]: _dropped, ...rest } = form.task_reasoning ?? {};
                      set('task_reasoning', e.target.value ? { ...rest, [t.id]: e.target.value as Reasoning } : rest);
                    }} />
                ))}
              </div>
            </div>
          ) : null}
          <Textarea label="When an assistant cannot help, it says" rows={2} maxLength={500}
            hint="Unless the client wrote its own."
            value={form.fallback_message} onChange={(e) => set('fallback_message', e.target.value)} />
          {canEdit ? (
            <div className="flex justify-end">
              <Button loading={save.isPending} disabled={Boolean(contextError)} onClick={() => save.mutate()}>Save platform rules</Button>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </ReadOnly>
  );
}

function PricingCard({ initial }: { initial: Pricing }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.ai.edit');
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);
  const save = useMutation({
    mutationFn: () => api.put<Overview>('/messaging/ai/pricing', form),
    onSuccess: (data) => {
      queryClient.setQueryData(aiKeys.overview, data);
      toast.success('Rate card saved');
    },
    onError: (e) => toast.error('Not saved', e instanceof ApiError ? e.message : undefined),
  });
  const setModel = (index: number, patch: Partial<Pricing['models'][number]>) =>
    setForm((f) => ({ ...f, models: f.models.map((m, i) => (i === index ? { ...m, ...patch } : m)) }));
  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader title="Rate card" description="What a client pays, per million tokens, in rupees — unless it has its own price, set on its page. The longest matching model prefix wins; otherwise the default." />
        <CardBody className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <Input label="Default — input (₹ / million tokens)" inputMode="decimal"
              value={form.input_per_million} onChange={(e) => setForm({ ...form, input_per_million: e.target.value })} />
            <Input label="Default — output (₹ / million tokens)" inputMode="decimal"
              value={form.output_per_million} onChange={(e) => setForm({ ...form, output_per_million: e.target.value })} />
          </div>
          {form.models.map((rate, index) => (
            <div key={index} className="flex items-end gap-2">
              <Input size="sm" label="Model prefix" className="font-mono" placeholder="anthropic:claude-haiku"
                containerClassName="flex-1" value={rate.match} onChange={(e) => setModel(index, { match: e.target.value })} />
              <Input size="sm" label="Input ₹/M" inputMode="decimal" containerClassName="w-32"
                value={rate.input_per_million} onChange={(e) => setModel(index, { input_per_million: e.target.value })} />
              <Input size="sm" label="Output ₹/M" inputMode="decimal" containerClassName="w-32"
                value={rate.output_per_million} onChange={(e) => setModel(index, { output_per_million: e.target.value })} />
              {canEdit ? (
                <Button size="sm" variant="ghost" aria-label="Remove"
                  onClick={() => setForm({ ...form, models: form.models.filter((_, i) => i !== index) })}>
                  <Trash2 className="size-3.5" />
                </Button>
              ) : null}
            </div>
          ))}
          {canEdit ? (
            <div className="flex justify-between">
              <Button size="sm" variant="secondary"
                onClick={() => setForm({ ...form, models: [...form.models, { match: '', input_per_million: '0', output_per_million: '0' }] })}>
                <Plus className="size-3.5" />
                Rate for a model
              </Button>
              <Button loading={save.isPending} onClick={() => save.mutate()}>Save rate card</Button>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </ReadOnly>
  );
}
